import moment from "moment";
import { useEffect, useState } from "react";
import { getComplaintsApi, updateComplaintStatusApi } from "../../api/appeal";
import Breadcrumb from "../../component/breadcrumb";
import Datatable from "../../component/datatable";
import Pagination from "../../component/pagination";
import { IComplaint, IComplaintFilter } from "../../interface/appeal";
import { IUserSlim } from "../../interface/user";
import { setAppLoading } from "../../redux/app.reducer";
import { useAppDispatch } from "../../redux/hooks";
import useSnackbar from "../../utils/snackbar";
import { useNavigate } from "react-router-dom";

const ComplaintList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [openSnackbar] = useSnackbar({ position: "bottom-left" });
  const [loading, setLoading] = useState(true);
  const [complaints, setComplaints] = useState<IComplaint[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [filter, setFilter] = useState<IComplaintFilter>({
    begin: moment().format("YYYY-MM-DD"),
    end: moment().format("YYYY-MM-DD"),
    status: "",
  });

  const fetchComplaints = async (filter: IComplaintFilter) => {
    try {
      setLoading(true);
      const { data } = await getComplaintsApi(filter);
      setComplaints(data.results ?? []);
      setPagination({
        page: data.meta.page,
        limit: data.meta.perPage,
        total: data.meta.totalCount,
        totalPages: parseInt(
          (data.meta.totalCount / data.meta.perPage).toFixed(0)
        ),
      });
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Something went wrong.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (
    id: string,
    status: "resolved" | "rejected",
    note?: string
  ) => {
    try {
      setLoading(true);
      await updateComplaintStatusApi(id, { status, resolveNote: note });
      await fetchComplaints(filter);
      openSnackbar("Status updated successfully");
    } catch (e: any) {
      let msg = e?.response?.data?.message || "Error: Failed to update status.";
      openSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComplaints(filter);
  }, [filter]);

  useEffect(() => {
    dispatch(setAppLoading(loading));
  }, [loading, dispatch]);

  return (
    <>
      <Breadcrumb
        title={"ระบบร้องเรียน"}
        children={[{ title: "หน้าแรก", link: "/" }, { title: "ระบบร้องเรียน" }]}
      />

      <div className="row">
        <h4 className="mb-4 ml-3" style={{ marginTop: 5 }}>
          {moment().format("DD MMMM YYYY")}
        </h4>
        <div className="col-lg-8 my-1 ml-auto mr-3">
          <div className="d-flex align-items-center justify-content-end">
            <div className="d-flex align-items-center">
              <div className="input-group input-daterange mr-3 d-flex align-items-center">
                <input
                  type="date"
                  className="form-control"
                  value={filter.begin}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, begin: e.target.value }))
                  }
                  style={{ width: "300px" }}
                />
                <div className="input-group-addon mx-2">ถึง</div>
                <input
                  type="date"
                  className="form-control"
                  value={filter.end}
                  onChange={(e) =>
                    setFilter((prev) => ({ ...prev, end: e.target.value }))
                  }
                  style={{ width: "300px" }}
                />
              </div>
              <select
                className="form-control mr-3"
                value={filter.status}
                onChange={(e) =>
                  setFilter((prev) => ({ ...prev, status: e.target.value }))
                }
                style={{ maxWidth: "300px" }}
              >
                <option value="">ทั้งหมด</option>
                <option value="pending">รอดำเนินการ</option>
                <option value="resolved">ดำเนินการแล้ว</option>
                <option value="rejected">ปฏิเสธ</option>
              </select>
            </div>
            <button
              className="btn btn-primary"
              onClick={() =>
                navigate(`${process.env.PUBLIC_URL}/appeal/topics`)
              }
            >
              จัดการหัวข้อร้องเรียน
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 mb-3">
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-12 mb-2">
                  <h4 className="mt-4 ml-1">
                    ทั้งหมด {pagination.total.toLocaleString("th-TH")} รายการ
                  </h4>
                </div>
              </div>
              <div>
                <Datatable
                  columns={[
                    {
                      title: "วันที่",
                      width: 150,
                      data: "submittedAt",
                      render(data: string) {
                        return moment(data).format("DD/MM/YYYY HH:mm");
                      },
                    },
                    {
                      title: "ผู้ร้องเรียน",
                      width: 200,
                      data: "user",
                      render(data: IUserSlim) {
                        return `${data.firstName} ${data.lastName}`;
                      },
                    },
                    {
                      title: "หัวข้อ",
                      width: 200,
                      data: "topic",
                      render(data) {
                        return data.label;
                      },
                    },
                    {
                      title: "รายละเอียด",
                      data: "description",
                      width: 250,
                      render(data: string) {
                        return `<div class="line-clamp-2">${data}</div>`;
                      },
                    },
                    {
                      title: "สถานะ",
                      width: 120,
                      data: "status",
                      render(data: string) {
                        const statusMap = {
                          pending:
                            '<span class="badge badge-warning">รอดำเนินการ</span>',
                          resolved:
                            '<span class="badge badge-success">ดำเนินการแล้ว</span>',
                          rejected:
                            '<span class="badge badge-danger">ปฏิเสธ</span>',
                        };
                        return (
                          statusMap[data as keyof typeof statusMap] || data
                        );
                      },
                    },
                    {
                      title: "การจัดการ",
                      width: 200,
                      data: null,
                      render(data: any, type: any, row: IComplaint) {
                        if (row.status === "pending") {
                          return `
                            <div class="d-flex">
                              <button class="btn btn-success btn-sm mr-2" onclick="handleResolve('${row.id}')">
                                ดำเนินการแล้ว
                              </button>
                              <button class="btn btn-danger btn-sm" onclick="handleReject('${row.id}')">
                                ปฏิเสธ
                              </button>
                            </div>
                          `;
                        }
                        return row.resolveNote || "-";
                      },
                    },
                  ]}
                  data={complaints}
                />

                <Pagination
                  active={pagination.page}
                  limit={pagination.limit}
                  onPageChange={(page) => {
                    setFilter((prev) => ({ ...prev, page }));
                  }}
                  pageRangeDisplayed={5}
                  total={pagination.total}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add global functions for button clicks */}
      <script
        dangerouslySetInnerHTML={{
          __html: `
          function handleResolve(id) {
            const note = prompt('กรุณาระบุรายละเอียดการดำเนินการ:');
            if (note !== null) {
              window.complaintList.handleStatusUpdate(id, 'resolved', note);
            }
          }

          function handleReject(id) {
            const note = prompt('กรุณาระบุเหตุผลที่ปฏิเสธ:');
            if (note !== null) {
              window.complaintList.handleStatusUpdate(id, 'rejected', note);
            }
          }

          window.complaintList = {
            handleStatusUpdate: ${handleStatusUpdate.toString()}
          };
        `,
        }}
      />
    </>
  );
};

export default ComplaintList;
